import * as React from 'react';
import { Database, FileText } from 'lucide-react';
import { PostureChip, type PostureStatus } from '@/components/ui/posture-chip';
import { avatarTone } from '@/components/clients/team-avatars';
import { cn } from '@/lib/utils/cn';
import type { CoverageStatus } from '@/lib/api/discovery';
import { initials, meterCells } from './discovery-logic';

/**
 * Coverage pieces shared by the Discovery screens (Overview now, Findings
 * and the client pages later): the status chip, the source meter, and the
 * avatars / document badges for sources. Mirrors `covChip`, `meter`,
 * `sourcesStack` and `.docbadge` in `docs/discovery/prototype/`.
 */

const STATUS_POSTURE: Record<CoverageStatus, PostureStatus> = {
  corroborated: 'healthy',
  partial: 'at-risk',
  none: 'unassessed',
};

/** One set of words everywhere (Decision 53.6): the label is the API's `status_label`. */
export function CoverageStatusChip({ status, label }: { status: CoverageStatus; label: string }) {
  return (
    <PostureChip status={STATUS_POSTURE[status]} dot aria-label={label}>
      {label}
    </PostureChip>
  );
}

export function CoverageMeter({
  sources,
  required,
  status,
}: {
  sources: number;
  required: number;
  status: CoverageStatus;
}) {
  return (
    <span
      className="inline-flex items-center gap-[3px] text-meta text-ink-500"
      title={`${sources} of ${required} independent sources`}
    >
      {meterCells(sources, required, status).map((cell, i) => (
        <i
          key={i}
          aria-hidden="true"
          className={cn(
            'h-1.5 w-[18px] rounded-[3px]',
            cell === 'on'
              ? 'bg-posture-healthy'
              : cell === 'one'
                ? 'bg-posture-at-risk'
                : 'bg-ink-100',
          )}
        />
      ))}
      <b className="ml-1.5 font-semibold tabular-nums text-ink-900">{sources}</b>
      <span>/ {required}</span>
    </span>
  );
}

export function PersonAvatar({ id, name, size = 22 }: { id: string; name: string; size?: number }) {
  const tone = avatarTone({ id, name });
  return (
    <span
      title={name}
      className={cn(
        'inline-flex flex-none items-center justify-center rounded-full border border-paper-0 font-semibold',
        `bg-avatar-${tone}-bg text-avatar-${tone}-ink`,
      )}
      style={{ width: size, height: size, fontSize: Math.round(size * 0.42) }}
    >
      <span aria-hidden="true">{initials(name)}</span>
      <span className="sr-only">{name}</span>
    </span>
  );
}

export function DocBadge({ name, data = false }: { name: string; data?: boolean }) {
  const Icon = data ? Database : FileText;
  return (
    <span
      title={name}
      className="inline-flex h-[22px] w-[22px] flex-none items-center justify-center rounded-chip border border-cite-chip-border bg-cite-chip-bg text-cite-chip-ink"
    >
      <Icon className="h-3 w-3" aria-hidden="true" />
      <span className="sr-only">{name}</span>
    </span>
  );
}

export function SourceStack({
  sources,
  max = 4,
}: {
  sources: ReadonlyArray<{ key: string; kind: 'person' | 'document'; id: string; name: string }>;
  max?: number;
}) {
  if (sources.length === 0) return <span className="text-meta text-ink-500">None yet</span>;
  return (
    <span className="inline-flex items-center">
      {sources.slice(0, max).map((s, i) => (
        <span key={s.key} className={i > 0 ? '-ml-[5px]' : undefined}>
          {s.kind === 'document' ? (
            <DocBadge name={s.name} />
          ) : (
            <PersonAvatar id={s.id} name={s.name} />
          )}
        </span>
      ))}
      {sources.length > max ? (
        <span className="ml-1 text-meta text-ink-500">+{sources.length - max}</span>
      ) : null}
    </span>
  );
}

/** The 8px coverage dot in front of an area in a list (`.mx-st`). */
export function CoverageDot({ status }: { status: CoverageStatus }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'h-2 w-2 flex-none rounded-full',
        status === 'corroborated'
          ? 'bg-posture-healthy'
          : status === 'partial'
            ? 'bg-posture-at-risk'
            : 'bg-ink-200',
      )}
    />
  );
}

/** How many items wait for review (`.rv-count`), in the AI tone. */
export function ReviewCount({ count }: { count: number }) {
  return (
    <span
      title="Waiting for your review"
      className="h-5 min-w-5 flex-none rounded-full border border-ai-surface-border bg-ai-surface px-1.5 text-center text-[11px] font-semibold leading-[18px] text-ai-ink"
    >
      {count.toLocaleString()}
      <span className="sr-only"> waiting for your review</span>
    </span>
  );
}
